<template>
  <div :class="{ 'disabled': disabled, 'non-selectable': disabled }" @click="$emit('click')">
    <span
      ref="duration"
      class="text-faded q-px-sm q-py-xs cursor-pointer round-borders hover-highlight"
    >
      {{ durationFormatted }}
    </span>

    <validating-popover
      class="duration-popover"
      :disabled="disabled"
      @show="handleShowPopover"
      @hide="handleHidePopover"
    >
      <div class="q-pa-md">
        <div class="row items-center gutter-x-sm">
          <div class="col">
            <div class="block q-mb-sm">
              <span class="q-subheading text-faded">{{ $t('start') }}</span>
            </div>

            <time-input
              v-model="startedAtTime"
              v-validate.disable="'required|length:4'"
              name="startedAt"
              :date="startedAtDate"
              @clickDate="handleSelectedDate('start')"
            />
          </div>

          <div class="col">
            <div class="block q-mb-sm">
              <span class="q-subheading text-faded">{{ $t('stop') }}</span>
            </div>

            <time-input
              v-model="stoppedAtTime"
              v-validate.disable="isStopped ? 'required|length:4' : ''"
              name="stoppedAt"
              :date="stoppedAtDate"
              @clickDate="handleSelectedDate('stop')"
            />
          </div>
        </div>

        <div v-if="errors.has('startedAt') || errors.has('stoppedAt')" class="text-negative q-mt-sm">
          {{ errors.first('startedAt') || errors.first('stoppedAt') }}
        </div>
      </div>

      <q-datetime-picker
        v-model="date"
        minimal
        class="no-border"
      />
    </validating-popover>
  </div>
</template>

<script>
  import { convertToUTC, parseDateTime } from 'src/utils/date'
  import TimeInput from './TimeInput.vue'
  import ValidatingPopover from '../ValidatingPopover.vue'
  import { date } from 'quasar'
  import { secondsToHumanReadable } from 'src/utils/format'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    props: {
      duration: {
        required: true
      },

      startedAt: {
        required: true
      },

      stoppedAt: {
        required: false
      },

      disabled: {
        type: Boolean,
        default: false
      }
    },

    data () {
      return {
        startedAtTime: '',
        stoppedAtTime: '',
        startedAtDate: null,
        stoppedAtDate: null,
        selectedDateIsStartDate: true
      }
    },

    computed: {
      durationFormatted () {
        return secondsToHumanReadable(this.duration)
      },

      date: {
        get () {
          return this.selectedDateIsStartDate ? this.startedAtDate : this.stoppedAtDate
        },

        set (value) {
          if (this.selectedDateIsStartDate) {
            this.startedAtDate = value
          } else {
            this.stoppedAtDate = value
          }
        }
      },

      dateOrTimeChanged () {
        const originalStartedAt = parseDateTime(this.startedAt)
        const originalStoppedAt = parseDateTime(this.stoppedAt)

        const isSameTime = (originalDate, newTime) => {
          return date.formatDate(parseDateTime(originalDate), 'HHmm') === newTime
        }

        return !date.isSameDate(originalStartedAt, this.startedAtDate, 'day') ||
          !isSameTime(this.startedAt, this.startedAtTime) ||
          (this.isStopped && !date.isSameDate(originalStoppedAt, this.stoppedAtDate, 'day')) ||
          (this.isStopped && !isSameTime(this.stoppedAt, this.stoppedAtTime))
      },

      startedAtDateAndTimeUTC () {
        return convertToUTC(date.adjustDate(this.startedAtDate, {
          hours: this.startedAtTime.substring(0, 2),
          minutes: this.startedAtTime.substring(2),
          seconds: 0
        }))
      },

      stoppedAtDateAndTimeUTC () {
        return convertToUTC(date.adjustDate(this.stoppedAtDate, {
          hours: this.stoppedAtTime.substring(0, 2),
          minutes: this.stoppedAtTime.substring(2),
          seconds: 0
        }))
      },

      isStopped () {
        return !!this.stoppedAt
      },

      hasStoppedTime () {
        return !!this.stoppedAtTime
      }
    },

    methods: {
      handleShowPopover () {
        const startedAt = parseDateTime(this.startedAt)
        const stoppedAt = this.isStopped ? parseDateTime(this.stoppedAt) : new Date()

        this.startedAtDate = startedAt
        this.stoppedAtDate = stoppedAt
        this.startedAtTime = date.formatDate(startedAt, 'HHmm')
        this.stoppedAtTime = this.isStopped ? date.formatDate(stoppedAt, 'HHmm') : null

        this.errors.clear()
      },

      handleHidePopover (closePopover) {
        if (!this.dateOrTimeChanged) {
          closePopover()

          return
        }

        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              return
            }

            // Manually validate that start is before end because vee-validate does not work
            if (this.hasStoppedTime && date.getDateDiff(this.startedAtDateAndTimeUTC, this.stoppedAtDateAndTimeUTC, 'seconds') > 0) {
              this.errors.add({
                field: 'startedAt',
                msg: this.$t('start_before_end')
              })

              return
            }

            const data = {
              started_at: date.formatDate(this.startedAtDateAndTimeUTC, 'YYYY-MM-DD HH:mm:ss'),
              stopped_at: this.hasStoppedTime ? date.formatDate(this.stoppedAtDateAndTimeUTC, 'YYYY-MM-DD HH:mm:ss') : null
            }

            this.$emit('save', data)

            closePopover()
          })
      },

      handleSelectedDate (date) {
        this.selectedDateIsStartDate = date === 'start'
      }
    },

    components: {
      TimeInput,
      ValidatingPopover
    }
  }
</script>

<style lang="stylus">
  .duration-popover
    width: 290px
    .q-datetime-selector
      height: 285px !important
</style>
